import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import SignupPage from './components/SignupPage';
import LoginPage from './components/LoginPage';
import AdminLoginPage from './components/AdminLoginPage';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import MainLayout from './components/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import MaterialMaster from './components/MaterialMaster';
import CustomerMaster from './components/CustomerMaster';
import CustomerTransaction from './components/CustomerTransaction';
import BranchSelectorPage from './components/BranchSelectorPage';
import SupplierMaster from './components/SupplierMaster';
import SupplierTransaction from './components/SupplierTransaction';
import AccountMaster from './components/AccountMaster';
import PurchaseEntry from './components/PurchaseEntry';
import Rojmel from './components/Rojmel';
import OutstandingReport from './components/OutstandingReport';
import DataRepairTool from './components/DataRepairTool';
import { AuthProvider, readSession, writeSession } from './components/AuthContext';
import { getSubdomain, isCentralDomain } from './api';

import { Box, Typography, Grid, Paper, Card, CardContent } from '@mui/material';

// ─── Session Transfer Handler ─────────────────────────────────────────────────
// When a user logs in from the central domain and gets redirected to a tenant
// subdomain, their session data is passed as a base64 URL param.
// This function reads it, stores it in localStorage, then cleans the URL.
const handleSessionTransfer = () => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('session_transfer');
    if (!raw) return;

    try {
        const { session, role, token } = JSON.parse(atob(raw));
        if (session) {
            writeSession({ ...session, role });
            localStorage.setItem('csm_token', token);
        }
    } catch (e) {
        console.error('Session transfer failed:', e);
    }

    // Clean the URL — remove the ugly session_transfer param
    params.delete('session_transfer');
    const cleanUrl = window.location.pathname + (params.toString() ? `?${params}` : '');
    window.history.replaceState({}, '', cleanUrl);
};

// Run once on page load
handleSessionTransfer();

// ─── App ─────────────────────────────────────────────────────────────────────
const App = () => {
    const [subdomain] = useState(getSubdomain());
    const isCentral = isCentralDomain();

    const [activeTab, setActiveTab] = useState('डॅशबोर्ड');

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = '/login';
    };

    // Helper to render active component in the dashboard area
    const renderContent = () => {
        switch (activeTab) {
            case 'डॅशबोर्ड':
                return (
                    <Box sx={{ p: 4 }}>
                        {/* <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1B5E20' }}>
                            Welcome back to {subdomain?.toUpperCase()}!
                        </Typography> */}
                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ bgcolor: '#E8F5E9', borderLeft: '6px solid #2E7D32' }}>
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>एकूण ग्राहक</Typography>
                                        <Typography variant="h3" sx={{ color: '#1B5E20', fontWeight: 'bold' }}>२४</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ bgcolor: '#FFF3E0', borderLeft: '6px solid #EF6C00' }}>
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>आजचे व्यवहार</Typography>
                                        <Typography variant="h3" sx={{ color: '#E65100', fontWeight: 'bold' }}>१२</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ bgcolor: '#E3F2FD', borderLeft: '6px solid #1565C0' }}>
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>स्टॉक मधील साहित्य</Typography>
                                        <Typography variant="h3" sx={{ color: '#0D47A1', fontWeight: 'bold' }}>४५०</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                );
            case 'साहित्य मास्टर':
                return <MaterialMaster />;
            case 'ग्राहक मास्टर':
                return <CustomerMaster />;
            case 'ग्राहक व्यवहार (Rental)':
                return <CustomerTransaction />;
            case 'व्यापारी मास्टर':
                return <SupplierMaster />;
            case 'व्यापारी व्यवहार':
                return <SupplierTransaction />;
            case 'खाते मास्टर':
                return <AccountMaster />;
            case 'खरेदी व्यवहार':
                return <PurchaseEntry />;
            case 'जमा-खर्च / रोजमेळ':
                return <Rojmel />;
            case 'आउटस्टँडिंग साहित्य आणि अमाऊंट येणे':
                return <OutstandingReport />;
            case 'डेटा Repair Tool':
                return <DataRepairTool />;
            default:
                return (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h5" color="textSecondary">
                            {activeTab} पेज लवकरच येत आहे...
                        </Typography>
                    </Box>
                );
        }
    };

    // ── Central domain (no subdomain) ────────────────────────────────────────
    if (isCentral) {

        return (
            <AuthProvider>
                <Routes>
                    <Route path="/" element={<SignupPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/csmadmin" element={<AdminLoginPage />} />
                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute requiredRole="super_admin">
                                <SuperAdminDashboard onLogout={handleLogout} />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            </AuthProvider>
        );
    }

    // ── Tenant subdomain ──────────────────────────────────────────────────────
    return (
        <AuthProvider>
            <Routes>
                <Route path="/login" element={<LoginPage />} />

                {/* Branch Selector — Company Admin only, multi-branch companies */}
                <Route
                    path="/select-branch"
                    element={
                        <ProtectedRoute requiredRole="tenant">
                            <BranchSelectorPage />
                        </ProtectedRoute>
                    }
                />

                {/* Main Dashboard */}
                <Route
                    path="/dashboard"
                    element={
                        <ProtectedRoute requiredRole="tenant">
                            <MainLayout activeTab={activeTab} setActiveTab={setActiveTab} onLogout={handleLogout}>
                                {renderContent()}
                            </MainLayout>
                        </ProtectedRoute>
                    }
                />

                <Route path="*" element={<Navigate to="/login" />} />
            </Routes>
        </AuthProvider>
    );
};

export default App;
